<script setup>
// M26.2 / M26.5 Part 9 — workforce health rollup. `stats` is Part 9's full
// field set (Knowledge/Memory/Health/Latency/Queue/Failures/Response
// Time/Success Rate), computed by the page from data it already has —
// no new fetch here, and any field with nothing real behind it is null,
// rendered as — (workforceFormat.js), never a fabricated 0.
import { fmt, fmtMs } from './workforceFormat';

defineProps({
  summary: { type: Object, required: true }, // { healthy, degraded, attention }
  units: { type: Array, required: true },
  stats: { type: Object, default: () => ({}) },
});

const HEALTH_META = {
  healthy: {
    label: 'Healthy',
    dot: 'bg-success-500',
    text: 'text-success-700 dark:text-success-400',
  },
  degraded: {
    label: 'Degraded',
    dot: 'bg-amber-500',
    text: 'text-amber-700 dark:text-amber-400',
  },
  attention: {
    label: 'Needs attention',
    dot: 'bg-ruby-500',
    text: 'text-ruby-700 dark:text-ruby-400',
  },
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="rounded-xl border border-n-weak bg-n-background overflow-hidden">
    <div class="px-4 py-3 border-b border-n-weak">
      <h2
        class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider flex items-center gap-1.5"
      >
        <span class="i-lucide-heart-pulse size-3.5" />
        Workforce Health
      </h2>
    </div>

    <!-- Part 9 field set — real or —, never fabricated -->
    <div
      class="grid grid-cols-4 gap-y-3 px-4 py-3 border-b border-n-weak text-center"
    >
      <div>
        <p class="text-sm font-bold text-n-slate-12 tabular-nums">
          {{ fmt(stats.knowledgePercent, '%') }}
        </p>
        <p class="text-[10px] text-n-slate-7">Knowledge</p>
      </div>
      <div>
        <p class="text-sm font-bold text-n-slate-12 tabular-nums">
          {{ fmt(stats.memoryPercent, '%') }}
        </p>
        <p class="text-[10px] text-n-slate-7">Memory</p>
      </div>
      <div>
        <p class="text-sm font-bold text-n-slate-12 tabular-nums">
          {{ fmt(stats.healthPercent, '%') }}
        </p>
        <p class="text-[10px] text-n-slate-7">Health</p>
      </div>
      <div>
        <p class="text-sm font-bold text-n-slate-12 tabular-nums">
          {{ fmtMs(stats.latencyMs) }}
        </p>
        <p class="text-[10px] text-n-slate-7">Latency</p>
      </div>
      <div>
        <p class="text-sm font-bold text-n-slate-12 tabular-nums">
          {{ fmt(stats.queueCount) }}
        </p>
        <p class="text-[10px] text-n-slate-7">Queue</p>
      </div>
      <div>
        <p class="text-sm font-bold text-n-slate-12 tabular-nums">
          {{ fmt(stats.failuresCount) }}
        </p>
        <p class="text-[10px] text-n-slate-7">Failures</p>
      </div>
      <div>
        <p class="text-sm font-bold text-n-slate-12 tabular-nums">
          {{ fmtMs(stats.responseMs) }}
        </p>
        <p class="text-[10px] text-n-slate-7">Response Time</p>
      </div>
      <div>
        <p class="text-sm font-bold text-n-slate-12 tabular-nums">
          {{ fmt(stats.successRate, '%') }}
        </p>
        <p class="text-[10px] text-n-slate-7">Success Rate</p>
      </div>
    </div>

    <div class="grid grid-cols-3 divide-x divide-n-weak border-b border-n-weak">
      <div
        v-for="key in ['healthy', 'degraded', 'attention']"
        :key="key"
        class="px-3 py-3 text-center"
      >
        <p
          class="text-lg font-bold tabular-nums"
          :class="HEALTH_META[key].text"
        >
          {{ summary[key] || 0 }}
        </p>
        <p class="text-[11px] text-n-slate-7">{{ HEALTH_META[key].label }}</p>
      </div>
    </div>

    <div class="max-h-64 overflow-y-auto divide-y divide-n-weak">
      <div
        v-for="unit in units"
        :key="unit.key"
        class="flex items-center gap-2.5 px-4 py-2"
      >
        <span
          class="size-1.5 rounded-full shrink-0"
          :class="HEALTH_META[unit.health].dot"
        />
        <p class="text-xs text-n-slate-11 truncate flex-1">{{ unit.name }}</p>
        <span
          v-if="unit.failedRecentCount"
          class="text-[11px] text-ruby-600 dark:text-ruby-400 shrink-0"
        >
          {{ unit.failedRecentCount }} failed
        </span>
      </div>
    </div>
  </div>
</template>
